import { useCallback, useEffect, useState } from "react";
import { AppState } from "react-native";

import { nextCountdownTickAt, nextExpiryAt } from "@/lib/open-to-chat";

/**
 * The clock the Open-to-chat leases are judged against, and the thing that
 * moves it.
 *
 * State, not `Date.now()` in render: reading the clock during render is
 * impure and lets two rows in one list disagree about the same moment — the
 * clock is sampled at known points and handed down. And it must MOVE:
 * sampling only when data arrives froze a lapsed contact in the Open list and
 * held "I'm Open · for 30m" forever (the countdown did not expire late, it
 * stopped).
 *
 * ONE timeout, not a tick: a one-second interval re-renders the whole inbox
 * while mounted. Every moment that matters is known, so a single timeout is
 * armed for the soonest and re-armed as each passes; nothing armed when
 * nothing is open. Two kinds of moment, conflated once at the cost of a bug:
 * EXPIRIES (every lease on screen — decide truth: list membership, arcs) and
 * COUNTDOWN STEPS (the one displayed lease — decide only a label; missing
 * them shows something frozen, which reads as broken). Steps are
 * minute-resolution and only while a countdown is on screen.
 *
 * No tick touches the network — the lease timestamp is already in hand;
 * counting down is arithmetic. AppState resume closes the suspended-app gap
 * (iOS stops JS entirely; a lease that lapsed in a pocket is stale on
 * return) — same shape as use-authed-image.ts.
 */
export function useOpenClock(
  leases: readonly (string | null | undefined)[],
  /**
   * The one lease whose remaining time is currently DISPLAYED, if any.
   *
   * Only this lease earns per-minute wakeups, and only while its label is on
   * screen. Passing every lease here would wake the screen once a minute for
   * each open contact to redraw a ring that has no text on it.
   */
  countdownFor: string | null | undefined = null,
): {
  /** The instant every Open comparison on this screen is made against. */
  nowMs: number;
  /** Move the clock to now — call it whenever fresh leases have landed. */
  resample: () => void;
} {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const resample = useCallback(() => setNowMs(Date.now()), []);

  // Computed from the STATE clock, not from `Date.now()`, so this stays a pure
  // function of the render. It is a number or null, which makes it a stable
  // effect dependency — an array of leases would be a new identity every render
  // and would re-arm the timer on each one.
  const expiryAt = nextExpiryAt(leases, nowMs);
  const tickAt = nextCountdownTickAt(countdownFor, nowMs);
  // The soonest of the two. One timer, whichever kind of moment comes first.
  const nextAt =
    expiryAt === null || tickAt === null
      ? (expiryAt ?? tickAt)
      : Math.min(expiryAt, tickAt);

  useEffect(() => {
    if (nextAt === null) return;
    // A small margin past the boundary. Firing exactly on it can leave
    // `Date.now()` a millisecond short, and `isOpenNow` is a strict `>` — the
    // row would survive, the clock would move, and the next arm would be for
    // the same moment again. The margin costs a quarter second of accuracy on
    // a signal measured in half-hours.
    const delay = Math.max(0, nextAt - Date.now()) + 250;
    const timer = setTimeout(resample, delay);
    return () => clearTimeout(timer);
  }, [nextAt, resample]);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") resample();
    });
    return () => subscription.remove();
  }, [resample]);

  return { nowMs, resample };
}
